import * as React from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import { Icon, Steps } from 'antd';
import { STATUS_LABELS } from '../ContainerUtils';
import LightHouseIcon from 'src/components/common/icons/LightHouseIcon';
import { lastEventId } from '@sentry/browser';
import { tokens } from 'src/design-system';
import { connect } from 'react-redux';

const styles = theme => ({
  greenRing: {
    height: 19,
    width: 19,
    borderRadius: '100%',
    borderColor: tokens.colors.success,
    backgroundColor: tokens.colors.success,
    // border: '10px solid',
    marginRight: tokens.spacing.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  parentContainer: {
    '& p': {
      marginBottom: tokens.spacing.sm,
    },
  },
  tagDesign: {
    borderRadius: '16px',
    borderColor: tokens.colors.success,
    border: '1px solid',
    backgroundColor: 'transparent',
    height: '20px',
    color: tokens.colors.success,
    fontSize: tokens.fontSize.small,
    padding: '2px 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trackingTitle: {
    fontSize: tokens.fontSize.body,
    fontWeight: tokens.fontWeight.semibold,
    marginRight: tokens.spacing.md,
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  trackingDescription: {
    fontSize: tokens.fontSize.small,
    marginTop: tokens.spacing.sm,
    cursor: 'default',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  steps: {
    '& .ant-steps-item-tail': {
      backgroundColor: `${tokens.colors.success} !important`,
      '&:after': {
        backgroundColor: `${tokens.colors.success} !important`,
      },
    },
  },
  stepsGrey: {
    '& .ant-steps-item-tail': {
      backgroundColor: 'grey !important',
      '&:after': {
        backgroundColor: 'grey !important',
      },
    },
  },
  noTracking: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  containerNumber: {
    color: tokens.colors.textTertiary,
    fontWeight: tokens.fontWeight.semibold,
    fontSize: tokens.fontSize.body,
    lineHeight: '24px',
  },
});

class UnifiedTrackingTimeline extends React.Component<any, any> {
  handleClose = () => {
    this.props.onClose();
  }

  reportIssue = () => {
    this.props.reportIssue();
  }

  clubByLocation = (inputData, lastEventIndex) => {
    const outputData: any = [];

    let subArrayIndex = -1;
    if (inputData && inputData.length) {
      inputData.forEach((element, i) => {
        if (i === 0 || element.location !== inputData[i - 1].location ||
          (element.statusInternal !== inputData[i - 1].statusInternal &&
            element.statusInternal !== 'IN_TRANSIT')) {
          subArrayIndex = subArrayIndex + 1;
          outputData[subArrayIndex] = {
            active: i <= lastEventIndex,
            location: element.location,
            trackingEvents: [{
              timestamp: element.timestamp,
              event: element.event && element.event.length ? element.event.join(', ') : '',
              isPastEvent: element.isPastEvent,
              isNonShipmentEvent: element.isNonShipmentEvent,
            }],
            statusInternal: element.statusInternal,
            lastOriginalStatus: element.statusInternal,
          };
        } else {
          outputData[subArrayIndex].active = outputData[subArrayIndex].active ||
          (i <= lastEventIndex);
          outputData[subArrayIndex].lastOriginalStatus = element.statusInternal,
          outputData[subArrayIndex].trackingEvents.push({
            timestamp: element.timestamp,
            event: element.event && element.event.length ? element.event.join(', ') : '',
            isPastEvent: element.isPastEvent,
            isNonShipmentEvent: element.isNonShipmentEvent,
          });
        }
      });
    }
    return outputData;
  }

  statusInsert = (currentLocation, nextLocation) => {
    if (currentLocation.statusInternal === nextLocation.statusInternal &&
      ['AT_ORIGIN', 'REACHED_DESTINATION_PORT', 'TRANSSHIPMENT'].indexOf(
        currentLocation.statusInternal) > -1) {
      return {
        active: currentLocation.trackingEvents[currentLocation.trackingEvents.length - 1]
        .isPastEvent,
        statusInternal: currentLocation.statusInternal === 'TRANSSHIPMENT' ? 'IN_TRANSIT' :
        currentLocation.statusInternal,
        isNewEvent: true,
        dates: {
          from: currentLocation.trackingEvents[currentLocation.trackingEvents.length - 1].timestamp,
          to: nextLocation.trackingEvents[0].timestamp,
        },
        locations: {
          from: currentLocation.location,
          to: nextLocation.location,
        },
      };
    }
    if (currentLocation.statusInternal !== nextLocation.statusInternal && (
      currentLocation.statusInternal === 'AT_ORIGIN' ||
      nextLocation.statusInternal === 'REACHED_DESTINATION_PORT'
    ) && (
      currentLocation.lastOriginalStatus === 'IN_TRANSIT' ||
      nextLocation.trackingEvents[0].isPastEvent
      )) {
      return {
        active: currentLocation.trackingEvents[currentLocation.trackingEvents.length - 1]
        .isPastEvent,
        statusInternal: (currentLocation.statusInternal === 'AT_ORIGIN'
        && nextLocation.statusInternal === 'IN_TRANSIT') ? 'AT_ORIGIN' :
        'IN_TRANSIT',
        isNewEvent: true,
        dates: {
          from: currentLocation.trackingEvents[currentLocation.trackingEvents.length - 1].timestamp,
          to: nextLocation.trackingEvents[0].timestamp,
        },
        locations: {
          from: currentLocation.location,
          to: nextLocation.location,
        },
      };
    }
    return null;
  }

  checkLastEntry = (location) => {
    if ((
      location.statusInternal === 'TRANSSHIPMENT' && location.lastOriginalStatus === 'IN_TRANSIT') ||
      (location.statusInternal === 'AT_ORIGIN' && location.lastOriginalStatus === 'IN_TRANSIT')
    ) {
      return {
        active: location.trackingEvents[location.trackingEvents.length - 1].isPastEvent,
        statusInternal: 'IN_TRANSIT',
        isNewEvent: true,
        dates: {
          from: location.trackingEvents[location.trackingEvents.length - 1].timestamp,
        },
        locations: {
          from: location.location,
        },
      };
    }
    return null;
  }

  generateTracking = () => {
    const { tracking, currentEventIndex } = this.props;
    if (!tracking || !tracking.length) return null;
    const locationArr = this.clubByLocation(tracking, currentEventIndex);
    const arr: any[] = [];
    locationArr.forEach((location, index) => {
      arr.push(location);
      if (index < locationArr.length - 1) {
        const item = this.statusInsert(location, locationArr[index + 1]);
        if (item) {
          arr.push(item);
        }
      } else if (index === locationArr.length - 1) {
        const item = this.checkLastEntry(location);
        if (item) {
          arr.push(item);
        }
      }
    });
    return arr;
  }

  renderStepTitle = (tracking, isLastStep) => {
    const { classes } = this.props;
    const title = tracking.isNewEvent ?
    ((tracking.locations.from || tracking.locations.to) ?
    `${tracking.locations.from || 'N/A'} ${(isLastStep ? tracking.locations.to : true) ? '-' : ''} ${tracking.locations.to || (isLastStep ? '' : 'N/A')}` : '') :
    tracking.location || '';
    return <div style={{ cursor: 'default', display: 'flex', flexDirection: 'row',
      justifyContent: 'space-between' }}>
    {title ? <span className={classes.trackingTitle}>{title}</span> : null}
    {tracking.statusInternal && <span className={classes.tagDesign} style={{
      borderColor: tracking.active ? '#4BCF47' : 'grey',
      color: tracking.active ? '#4BCF47' : 'grey' }}>
    {STATUS_LABELS[tracking.statusInternal]}
    </span>}
    </div>;
  }

  getDateFormat = () => {
    const { showTrackingTimestamp } = this.props;
    return showTrackingTimestamp ? 'DD-MM-YYYY | hh:mm A' : 'DD-MM-YYYY';
  }

  renderTracking = () => {
    const { classes } = this.props;
    const trackingArr = this.generateTracking();
    if (!trackingArr) return null;
    const current = trackingArr.reduce((acc, itr) => acc + (itr.active ? 1 : 0), 0);
    return <Steps direction="vertical" size="small" current={current - 1}>
      {
        trackingArr.map((tracking, index) => <Steps.Step key={index}
        status={tracking.active ? 'process' : 'wait'}
        className={trackingArr[index + 1] &&
          trackingArr[index + 1].active ? classes.steps : classes.stepsGrey}
        icon={<Icon type="check-circle" theme="filled" style={{ fontSize: '24px',
          color: tracking.active ? '#4BCF47' : 'grey', backgroundColor: tokens.colors.bgWhite }}/>}
        title={this.renderStepTitle(tracking, index === trackingArr.length - 1)}
        description={tracking.isNewEvent ? <div className={classes.trackingDescription}
        style={{ color: `rgba(0, 0, 0, ${tracking.active ? 0.65 : 0.45}` }}>
          {tracking.dates.from ? moment(tracking.dates.from).utc().format(this.getDateFormat()) : null}
          &nbsp;
          { (index === trackingArr.length - 1 ? tracking.dates.to : true) ?  '-' : '' }
          &nbsp;
          {tracking.dates.to ? moment(tracking.dates.to).utc().format(this.getDateFormat()) : null}
        </div> : <div>
          {tracking.trackingEvents && tracking.trackingEvents.length ?
            tracking.trackingEvents.map((event, ind) => <div key={ind}
            className={classes.trackingDescription}
            style={{ color: `rgba(0, 0, 0, ${event.isNonShipmentEvent ? 0.45 : event.isPastEvent ? 0.65 : 0.45}`, fontStyle: `${event.isNonShipmentEvent ? 'italic' : 'normal'}`, fontWeight: event.isNonShipmentEvent ? 200 : 500 }}>
              <span style={{ display: 'flex', alignItems: 'center'}}>
                {event.isNonShipmentEvent ? '// ':''}
                {event.timestamp ? moment(event.timestamp).utc().format(this.getDateFormat()) : null}
              </span>
              <span style={{ marginLeft: '10px', display: 'flex', flex: 1,
                alignItems: 'center', wordBreak: 'break-word' }}>
                {event.event}</span>
            </div>)
            : null}
        </div>}/>)
      }
    </Steps>;
  }

  renderNoTracking = () => {
    const {
      classes,
      containerNumber,
    } = this.props;
    return <div
      className={classes.noTracking}>
      <LightHouseIcon
        width="200"
        height="200"
      />
      <span
        className={classes.containerNumber}>
        Container# {containerNumber}</span>
      <span>is not part of the BL and will not be tracked</span>
    </div>;
  }

  render() {
    const {
      classes,
      doNotShowTracking,
    } = this.props;
    if (doNotShowTracking) return this.renderNoTracking();
    return  <div
      className={classes.parentContainer}>
      <div style={{ padding: '20px' }}>
        {this.renderTracking()}
      </div>
    </div>;
  }
}
const mapStateToProps = ({masterData}) => {
  return {
    showTrackingTimestamp: masterData.showTrackingTimestamp
  };
};

const connector = connect(mapStateToProps);
export default injectSheet(styles)(connector(UnifiedTrackingTimeline));
